// src/pages/Production/SubcontractorBalanceReport.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';

const styles = {
    container: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: 'transparent'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px'
    },
    title: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#0f172a',
        margin: 0,
        letterSpacing: '-0.5px'
    },
    backLink: {
        textDecoration: 'none',
        color: '#06C167',
        fontSize: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    box: {
        background: 'white',
        padding: '32px',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0',
        marginBottom: '30px'
    },
    summaryCards: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '30px'
    },
    summaryCard: {
        background: '#f8fafc',
        borderRadius: '12px',
        padding: '20px',
        border: '1px solid #e2e8f0'
    },
    debtCard: {
        background: 'linear-gradient(135deg, #dc2626, #ef4444)',
        color: 'white',
        border: 'none'
    },
    creditCard: {
        background: 'linear-gradient(135deg, #059669, #10b981)',
        color: 'white',
        border: 'none'
    },
    cardTitle: {
        fontSize: '13px',
        color: '#64748b',
        marginBottom: '6px'
    },
    cardValue: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#0f172a'
    },
    table: {
        width: '100%',
        borderCollapse: 'separate',
        borderSpacing: 0,
        background: 'white',
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid #e2e8f0'
    },
    th: {
        background: '#f8fafc',
        padding: '14px 16px',
        textAlign: 'left',
        borderBottom: '1px solid #e2e8f0',
        fontWeight: '600',
        fontSize: '12px',
        color: '#374151',
        textTransform: 'uppercase'
    },
    td: {
        padding: '14px 16px',
        borderBottom: '1px solid #f1f5f9',
        fontSize: '14px',
        color: '#374151'
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '64px 32px',
        color: '#64748b'
    },
    emptyState: {
        textAlign: 'center',
        padding: '64px 32px',
        color: '#64748b'
    }
};

function SubcontractorBalanceReport() {
    const { userData } = useAuth();
    const [subcontractors, setSubcontractors] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!userData?.companyId) {
            setLoading(false);
            return;
        }

        const companyId = userData.companyId;

        const unsubSubcontractors = onSnapshot(
            query(collection(db, 'companies', companyId, 'suppliers')),
            snap => {
                // Filter only subcontractors (those with isSubcontractor flag or costTariff)
                const subs = snap.docs
                    .map(d => ({ id: d.id, ...d.data() }))
                    .filter(s => s.isSubcontractor || s.costTariff);
                setSubcontractors(subs);
                setLoading(false);
            }
        );

        return () => unsubSubcontractors();
    }, [userData]);

    const reportData = useMemo(() => {
        const totalBalance = subcontractors.reduce((sum, s) => sum + (s.balance || 0), 0);
        const debtors = subcontractors.filter(s => (s.balance || 0) > 0);
        const creditors = subcontractors.filter(s => (s.balance || 0) < 0);
        const totalDebt = debtors.reduce((sum, s) => sum + s.balance, 0);
        const totalCredit = Math.abs(creditors.reduce((sum, s) => sum + s.balance, 0));

        return {
            subcontractors: subcontractors.sort((a, b) => (b.balance || 0) - (a.balance || 0)),
            totals: {
                count: subcontractors.length,
                totalBalance,
                totalDebt,
                totalCredit
            }
        };
    }, [subcontractors]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.loadingContainer}>
                    <div>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
                        <p>Fason bakiye verileri yükleniyor...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <Link to="/uretim/raporlar" style={styles.backLink}>← Üretim Raporları</Link>
            </div>

            <div style={styles.box}>
                <h2 style={styles.title}>Fason Bakiyesi ve Maliyet Özeti</h2>
                <p style={{ color: '#64748b', marginTop: '8px', marginBottom: '24px' }}>
                    Fasoncuların bakiyeleri ve yapılan ödemeler
                </p>

                <div style={styles.summaryCards}>
                    <div style={styles.summaryCard}>
                        <div style={styles.cardTitle}>Toplam Fasoncu</div>
                        <div style={styles.cardValue}>{reportData.totals.count}</div>
                    </div>
                    <div style={{
                        ...styles.summaryCard,
                        ...(reportData.totals.totalDebt > 0 ? styles.debtCard : {})
                    }}>
                        <div style={{ ...styles.cardTitle, color: reportData.totals.totalDebt > 0 ? 'rgba(255,255,255,0.8)' : undefined }}>
                            Toplam Borç
                        </div>
                        <div style={{ ...styles.cardValue, color: reportData.totals.totalDebt > 0 ? 'white' : undefined }}>
                            {formatCurrency(reportData.totals.totalDebt)}
                        </div>
                    </div>
                    <div style={{
                        ...styles.summaryCard,
                        ...(reportData.totals.totalCredit > 0 ? styles.creditCard : {})
                    }}>
                        <div style={{ ...styles.cardTitle, color: reportData.totals.totalCredit > 0 ? 'rgba(255,255,255,0.8)' : undefined }}>
                            Toplam Alacak
                        </div>
                        <div style={{ ...styles.cardValue, color: reportData.totals.totalCredit > 0 ? 'white' : undefined }}>
                            {formatCurrency(reportData.totals.totalCredit)}
                        </div>
                    </div>
                    <div style={styles.summaryCard}>
                        <div style={styles.cardTitle}>Net Bakiye</div>
                        <div style={{
                            ...styles.cardValue,
                            color: reportData.totals.totalBalance >= 0 ? '#dc2626' : '#059669'
                        }}>
                            {formatCurrency(reportData.totals.totalBalance)}
                        </div>
                    </div>
                </div>

                {reportData.subcontractors.length === 0 ? (
                    <div style={styles.emptyState}>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>💰</div>
                        <h3 style={{ color: '#374151' }}>Fasoncu Bulunamadı</h3>
                        <p>Henüz fasoncu tanımlanmamış.</p>
                    </div>
                ) : (
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Fasoncu</th>
                                <th style={styles.th}>Telefon</th>
                                <th style={styles.th}>E-posta</th>
                                <th style={styles.th}>Tarife</th>
                                <th style={styles.th}>Bakiye</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reportData.subcontractors.map(sub => (
                                <tr key={sub.id}>
                                    <td style={{ ...styles.td, fontWeight: '600' }}>{sub.name}</td>
                                    <td style={styles.td}>{sub.phone || '-'}</td>
                                    <td style={styles.td}>{sub.email || '-'}</td>
                                    <td style={styles.td}>
                                        {sub.costTariff && sub.costTariff.length > 0
                                            ? sub.costTariff.map(t => `${t.value}₺/${t.unit}`).join(', ')
                                            : '-'
                                        }
                                    </td>
                                    <td style={{
                                        ...styles.td,
                                        fontWeight: '700',
                                        color: (sub.balance || 0) > 0 ? '#dc2626' : (sub.balance || 0) < 0 ? '#059669' : '#0f172a'
                                    }}>
                                        {formatCurrency(sub.balance || 0)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

export default SubcontractorBalanceReport;
